import React, { useState, useEffect, useImperativeHandle, forwardRef, useContext } from 'react';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { debounce } from 'lodash';
import { userService } from '../../services/userService';
import { Avatar } from '../common';
import VipIcon from '../Icons/VipIcons';
import { lightTheme, darkTheme } from '../../styles/theme';

// Styled components matching UserTaggingModal card style
const MentionDropdown = styled.div`
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border || 'rgba(225, 229, 235, 0.3)'};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-height: 320px;
  overflow-y: auto;
  min-width: 280px;
  max-width: 320px;
  padding: 8px;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid ${props => props.$selected ? (props.theme.colors.primary || '#007bff') : (props.theme.colors.border || 'rgba(225, 229, 235, 0.3)')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? 
    `${props.theme.colors.primary || '#007bff'}20` : 
    '#ffffff'
  };
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const UserInfo = styled.div`
  margin-left: 10px;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Username = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

// React component for mention suggestions
const MentionList = forwardRef(({ items, command, query, editor }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Get current theme from localStorage (where the app stores it) or styled-components
  const getTheme = () => {
    // Try to get from localStorage first
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') return darkTheme;
    if (storedTheme === 'light') return lightTheme;
    
    // Fallback: check body background color
    const bodyBg = window.getComputedStyle(document.body).backgroundColor;
    const isDark = bodyBg.includes('15, 15, 15') || bodyBg.includes('0, 0, 0');
    return isDark ? darkTheme : lightTheme;
  };
  
  const theme = getTheme();

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  const selectItem = (index) => {
    const item = items[index];
    if (item) {
      // Pass all necessary attributes for mention display and navigation
      // displayName/label is what gets displayed - use fullName
      // username is stored separately for click navigation
      const fullName = item.fullName || item.displayName || item.username;
      command({
        id: `${item.id}|${item.username}`,
        label: fullName,
        displayName: fullName,  // For MentionExtension.js compatibility
        username: item.username, // For data-username attribute
      });
    }
  };

  if (!items || items.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <MentionDropdown>
          <EmptyState>
            {query && query.length >= 3 
              ? `No users found matching "${query}"` 
              : 'Type at least 3 characters to search...'}
          </EmptyState>
        </MentionDropdown>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <MentionDropdown>
        {items.map((user, index) => (
          <UserCard
            key={user.id}
            $selected={index === selectedIndex}
            onMouseDown={(e) => {
              e.preventDefault();
              selectItem(index);
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <Avatar
              src={user.avatar || user.profilePicture}
              name={user.fullName || user.username}
              size="36px"
              userId={user.id}
              hideOnlineIndicator={true}
            />
            <UserInfo>
              <UserName>
                {user.fullName || user.displayName || user.username}
                {(user.isVerified || user.verified) && (
                  <VipIcon 
                    name="VerifiedBadge" 
                    size={14}
                  />
                )}
              </UserName>
              <Username>@{user.username}</Username>
            </UserInfo>
          </UserCard>
        ))}
      </MentionDropdown>
    </ThemeProvider>
  );
});

MentionList.displayName = 'MentionList';

// Debounced search function
const debouncedSearch = debounce(async (query, resolve) => {
  try {
    const result = await userService.searchUsersForTagging(query.trim());
    const users = result.content || result;
    resolve(users);
  } catch (error) {
    console.error('Error fetching users for mentions:', error);
    resolve([]);
  }
}, 300);

export const smartMentionSuggestion = {
  char: '@',
  allowSpaces: false,
  allowedPrefixes: [' ', '\n'],
  startOfLine: false,
  
  items: async ({ query }) => {
    // Require at least 3 characters before searching
    if (!query || query.trim().length < 3) {
      return [];
    }
    
    return new Promise((resolve) => {
      debouncedSearch(query, (users) => {
        const transformedUsers = users.map(user => ({
          id: user.id,
          username: user.username,
          displayName: user.fullName || user.username,
          fullName: user.fullName || user.username,
          avatar: user.profilePicture,
          isVerified: user.isVerified || user.verified || false
        }));
        resolve(transformedUsers);
      });
    });
  },

  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props: {
            ...props,
            query: props.query,
          },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          zIndex: 10000002,
          maxWidth: 'none',
        });
      },

      onUpdate: (props) => {
        component?.updateProps({
          ...props,
          query: props.query,
        });

        if (!props.clientRect) {
          return;
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }
        return component?.ref?.onKeyDown(props);
      },

      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
};

